
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { WelcomeForm } from "./welcome/WelcomeForm";
import { WelcomePricing } from "./welcome/WelcomePricing";
import { formSchema, WelcomeFormValues } from "./welcome/types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";

interface WelcomeDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WelcomeDialog({ isOpen, onClose }: WelcomeDialogProps) {
  const [step, setStep] = useState(1);
  const [hasChurchCode, setHasChurchCode] = useState(false);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const form = useForm<WelcomeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: "",
      congregation_name: "",
      bible_version: "KJV",
    },
    mode: "onChange",
  });

  useEffect(() => {
    const checkBraceletChurch = async () => {
      // Standardize on using 'id' parameter
      const braceletCode = searchParams.get('id') || searchParams.get('code');
      if (!braceletCode) return;
      
      const cleanCode = braceletCode.replace(/^GHG-?/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      
      // Handle TEST series bracelets directly
      if (cleanCode.startsWith('TEST')) {
        console.log("Found TEST series bracelet:", cleanCode);
        setHasChurchCode(true);
        form.setValue("congregation_name", "TEST COMM");
        return;
      }
      
      try {
        const { data: bracelet } = await supabase
          .from("bracelets")
          .select("church_code")
          .eq("referral_code", cleanCode)
          .maybeSingle();
        
        if (bracelet?.church_code) {
          const { data: churchBranding } = await supabase
            .from("church_branding")
            .select("church_name")
            .eq("church_code", bracelet.church_code)
            .maybeSingle();
          
          if (churchBranding?.church_name) {
            setHasChurchCode(true);
            form.setValue("congregation_name", churchBranding.church_name);
          }
        }
      } catch (error) {
        console.error("Error checking bracelet church:", error);
      }
    };
    
    checkBraceletChurch();
  }, [searchParams, form]);

  const onSubmit = async (values: WelcomeFormValues): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "Not authenticated. Please try signing in again.",
          variant: "destructive",
        });
        return;
      }

      console.log("Saving user profile data:", values);

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: values.full_name,
          congregation_name: values.congregation_name || "",
          bible_version: values.bible_version,
          onboarded: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) {
        console.error("Error updating profile:", error);
        throw error;
      }

      toast({
        title: "Welcome!",
        description: "Your profile has been saved successfully.",
      });
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: "Failed to save your profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFormNext = () => {
    const formValues = form.getValues();
    
    // Check if name is properly filled
    if (!formValues.full_name || formValues.full_name.trim().length < 2) {
      toast({
        title: "Please complete the form",
        description: "Your name is required to continue.",
        variant: "destructive",
      });
      return;
    }
    
    if (hasChurchCode) {
      onSubmit(formValues);
    } else {
      setStep(2);
    }
  };

  const handleSkipToPlan = async (): Promise<void> => {
    // Save the basic profile first, then redirect to premium page
    await onSubmit(form.getValues());
    navigate('/premium');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden bg-gradient-to-b from-white to-grace-light">
        <ScrollArea className="max-h-[90vh]">
          <div className="flex flex-col items-center p-6 space-y-6">
            <div className="w-full flex justify-center mb-4 animate-fade-in">
              <img
                src="/lovable-uploads/97cec83c-b4fa-48c3-a12a-a678346efa1f.png"
                alt="Grace Awakens Logo"
                className="w-32 h-32 object-contain"
              />
            </div>

            {step === 1 && (
              <WelcomeForm 
                form={form} 
                onNext={handleFormNext}
                hasChurchCode={hasChurchCode}
              />
            )}

            {step === 2 && (
              <WelcomePricing 
                onSubmit={handleSkipToPlan}
                formValues={form.getValues()}
              />
            )}

            <div className="flex items-center gap-2 mt-4">
              <span className={`w-2 h-2 rounded-full ${step === 1 ? 'bg-grace-DEFAULT' : 'bg-grace-light'}`} />
              <span className={`w-2 h-2 rounded-full ${step === 2 ? 'bg-grace-DEFAULT' : 'bg-grace-light'}`} />
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
